import {
  ChangeDetectorRef,
  Component,
  EventEmitter,
  HostListener,
  Input,
  OnInit,
  Output,
} from '@angular/core';
import { FormBuilder, FormGroup } from '@angular/forms';
import { Router } from '@angular/router';
import { TranslateService } from '@ngx-translate/core';
import { of } from 'rxjs';
import { IQuestionnairePage } from 'src/app/interfaces/Questionnaire/IQuestionnaire';
import { IGetAnswerQuestionnaire } from 'src/app/interfaces/Questionnaire/IQuestionnaireAnswers';
import { IQuestionnaireDetailed } from 'src/app/interfaces/Questionnaire/IQuestionnaireDetailed';
import { QuestionnaireHistoryItem } from 'src/app/interfaces/Questionnaire/IQuestionnaireHistory';
import { EpisodeOfCareService } from 'src/app/services/episodeOfCare.service';
import { PermissionService } from 'src/app/services/permission.service';
import { DynamicQuestionnaireService } from 'src/app/services/dynamicQuestionnaire.service';
import { SwalToastService } from 'src/app/services/swal.service';
import { buildDepartmentalQuestionnairePayload } from 'src/app/utils/departmental-questionnaire-submit';
import Swal from 'sweetalert2';
import { buildQuestionnaireDetailFromHistoryItem } from 'src/app/utils/questionnaire-history-adapter';

const TRUNK_CONTROL_TEST_QUESTIONNAIRE_TEMPLATE_ID = 21;

@Component({
  selector: 'app-trunk-control-test-questionnaire',
  templateUrl: './trunk-control-test-questionnaire.component.html',
  styleUrl: './trunk-control-test-questionnaire.component.css',
  standalone: false,
})
export class TrunkControlTestQuestionnaireComponent implements OnInit {
  formGroup: FormGroup;
  pages: IQuestionnairePage[] = [];
  answers!: IGetAnswerQuestionnaire;
  data!: IQuestionnaireDetailed;
  current = 0;
  disable = false;
  isLargeScreen = true;
  isLoading = false;
  @Output() QuestionnaireLoadingStatus = new EventEmitter<boolean>();
  formInitialized = false;
  initialFormValues: { [key: string]: any } = {};
  hasChanges = false;
  ignoreChanges = false;
  @Input() readonlyMode = false;
  @Input() historicalItem?: QuestionnaireHistoryItem | null;
  @Input() useHistoricalAnswers = false;
  @Input() startWithBlank = false;
  @Input() returnToTableAfterSubmit = false;
  @Output() questionnaireSubmitted = new EventEmitter<void>();

  constructor(
    private fb: FormBuilder,
    private readonly eocService: EpisodeOfCareService,
    private readonly router: Router,
    private readonly cdr: ChangeDetectorRef,
    private readonly translate: TranslateService,
    private readonly swalToastService: SwalToastService,
    private readonly permissionService: PermissionService,
    private readonly dynamicQuestionnaireService: DynamicQuestionnaireService,
  ) {
    this.formGroup = this.fb.group({});
  }

  @HostListener('window:resize', ['$event'])
  onResize(event?: Event): void {
    this.isLargeScreen = window.innerWidth >= 1900;
  }

  isInpatientView(): boolean {
    return (
      !(
        this.eocService.currentEocId() === null ||
        this.eocService.currentEocId() === ''
      ) && this.router.url.includes('inPatientView')
    );
  }

  isEOC() {
    return (
      !(
        this.eocService.currentEocId() === null ||
        this.eocService.currentEocId() === ''
      ) && this.router.url.includes('episodeOfCare')
    );
  }

  ngOnInit() {
    this.isLoading = true;
    this.QuestionnaireLoadingStatus.emit(this.isLoading);
    this.onResize();
    this.getData();
    this.formGroup.valueChanges.subscribe(() => {
      if (this.formInitialized) {
        this.checkFormChanges();
      }
    });
  }

  checkFormChanges(): void {
    if (this.readonlyMode) {
      this.hasChanges = false;
      this.dynamicQuestionnaireService.setUnsavedChanges(false);
      return;
    }

    if (
      !this.formInitialized ||
      Object.keys(this.initialFormValues).length === 0
    ) {
      return;
    }
    this.hasChanges = false;

    Object.keys(this.formGroup.controls).forEach((key) => {
      // The locked score fields are computed from the answers and can be a
      // step behind here, so only the answers count as changes.
      if (this.formGroup.get(key)?.disabled) {
        return;
      }

      const currentValue = this.formGroup.get(key)?.value;
      const initialValue = this.initialFormValues[key];

      // Avoid false negatives like "" !== null or 0 !== "0"
      const normalize = (val: any) =>
        val === null || val === undefined ? '' : val.toString();

      if (normalize(currentValue) !== normalize(initialValue)) {
        this.hasChanges = true;
      }
    });

    this.dynamicQuestionnaireService.setUnsavedChanges(this.hasChanges);
  }

  getData(): void {
    this.ignoreChanges = true;
    const historicalData =
      this.useHistoricalAnswers && this.historicalItem
        ? buildQuestionnaireDetailFromHistoryItem(
            this.historicalItem,
            TRUNK_CONTROL_TEST_QUESTIONNAIRE_TEMPLATE_ID,
          )
        : null;

    (historicalData
      ? of(historicalData)
      : this.startWithBlank
        ? this.dynamicQuestionnaireService.getBlankQuestionnaireData(
            TRUNK_CONTROL_TEST_QUESTIONNAIRE_TEMPLATE_ID,
          )
        : this.dynamicQuestionnaireService.getQuestionnaireData(
            TRUNK_CONTROL_TEST_QUESTIONNAIRE_TEMPLATE_ID,
          )
    ).subscribe((data: IQuestionnaireDetailed) => {
      if (data && data.questionnaire) {
        this.data = data;
        this.pages = data.questionnaire.pages;
        this.pages.forEach((page) => {
          page.elements.forEach((question) => {
            this.formGroup.addControl(
              question.name,
              this.fb.control({
                value: '',
                disabled:
                  this.readonlyMode ||
                  (!this.isInpatientView() && !this.isEOC()) ||
                  !this.hasPermission(['a-sqa']),
              }),
            );
            // If there are answers in the response prefill the questionnaire
            if (data.answers) {
              data.answers.answer.forEach((ans) => {
                if (ans.questionTemplateId === question.id) {
                  // Check if the question type is boolean and convert the value accordingly
                  let value =
                    ans.questionType.toLowerCase() === 'boolean'
                      ? ans.value === 'true'
                      : ans.value;

                  this.formGroup.get(question.name as string)?.setValue(value);
                }
              });
            }
            // Save the initial value snapshot (after prefilling answers)
            this.initialFormValues[question.name] = this.formGroup.get(
              question.name,
            )?.value;
          });
        });
      }
      this.isLoading = false; // <-- Set loading to false after data is loaded
      setTimeout(() => {
        this.QuestionnaireLoadingStatus.emit(this.isLoading); // Delayed emit
      }, 500);
      this.formInitialized = true;
      this.ignoreChanges = false;
      if (this.readonlyMode) {
        this.dynamicQuestionnaireService.setUnsavedChanges(false);
      }
      this.checkFormChanges();
      this.cdr.detectChanges();
    });
  }

  onIndexChange(index: number): void {
    this.current = index;
    this.cdr.detectChanges();
  }

  onSubmit(): void {
    this.isLoading = true;
    const qaObj = buildDepartmentalQuestionnairePayload(
      TRUNK_CONTROL_TEST_QUESTIONNAIRE_TEMPLATE_ID,
      this.pages,
      this.formGroup.getRawValue(),
    );
    this.dynamicQuestionnaireService
      .saveQuestionnaireAnswers(this.data, qaObj)
      .subscribe({
        next: () => {
          this.swalToastService.toastSuccess(
            'Questionnaire answers added successfully',
          );
          this.hasChanges = false;
          this.formGroup.markAsPristine();
          this.dynamicQuestionnaireService.setUnsavedChanges(false);
          if (this.returnToTableAfterSubmit) {
            this.questionnaireSubmitted.emit();
          } else {
            this.getData();
          }
          this.isLoading = false;
        },
        error: (err) => {
          this.isLoading = false;
          this.swalToastService.toastError(
            'Unable to add Questionnaire answers',
          );
        },
      });
  }

  hasPermission(permission: string[]): boolean {
    return this.permissionService.can(permission);
  }

  revertForm(): void {
    Swal.fire({
      title: this.translate.instant('Revert changes?'),
      text: this.translate.instant(
        'Are you sure you want to discard all changes and revert to the initial values?',
      ),
      icon: 'warning',
      showCancelButton: true,
      confirmButtonText: this.translate.instant('Yes, revert'),
      cancelButtonText: this.translate.instant('Cancel'),
    }).then((result) => {
      if (result.isConfirmed) {
        Object.keys(this.initialFormValues).forEach((key) => {
          if (this.formGroup.contains(key)) {
            this.formGroup.get(key)?.setValue(this.initialFormValues[key]);
            this.formGroup.get(key)?.markAsPristine();
            this.formGroup.get(key)?.markAsUntouched();
          }
        });

        this.dynamicQuestionnaireService.setUnsavedChanges(false);
        this.swalToastService.toastSuccess(
          this.translate.instant('Changes reverted'),
        );
      }
    });
  }
}
